import type { Confirmation, ReportFlag, ReportLine } from './types'

/** A single line is "review" (red) if it's backordered or any comparison
 *  isn't a clean match. Shared by the list Flags count and the detail badges. */
export function lineNeedsReview(confirmedQty: number, checks?: ReportLine['checks']): boolean {
  if (confirmedQty === 0 || checks?.quantity?.status === 'BACKORDER') return true
  return (['quantity', 'unitPrice', 'extPrice'] as const).some(k => {
    const s = checks?.[k]?.status
    return s && s !== 'MATCH' && s !== 'UNKNOWN'
  })
}

/** A supplier line that is not on the PO in Epicor — usually a misread line number. The check could not match
 *  it, or the PO's own lines (read for every confirmation since 2026-09-14) do not include it. Red, like a
 *  difference; PO lines the supplier did not mention are the opposite case and are never flagged. */
export function lineNotOnPo(c: Confirmation, poLine: number, reportLine?: ReportLine): boolean {
  if (reportLine?.matched === false) return true
  const po = c.poSnapshot?.lines ?? []
  return po.length > 0 && !po.some(l => l.poLine === poLine)
}

/** How many lines on this confirmation need review — the left-list Flags count and the sidebar badge. */
export function reviewLineCount(c: Confirmation): number {
  const byLine = new Map<number, ReportLine>((c.report?.lines ?? []).map(l => [l.poLine, l]))
  return (c.extracted?.lines ?? []).filter(l => {
    const reportLine = byLine.get(l.poLine)
    return lineNeedsReview(l.confirmedQty, reportLine?.checks) || lineNotOnPo(c, l.poLine, reportLine)
  }).length
}

// Flag types that are advisory / informational / redundant with what the
// per-line comparison already shows, so they only clutter the Flags panel:
//  - SHIPVIA_REVIEW      — ship-via is flag-only anyway and already shown per line
//  - NO_OPEN_RELEASE     — LOW, informational
//  - BACKORDER_UNCONFIRMED — already shown as the line's Backorder qty pill
const HIDDEN_FLAG_TYPES = new Set(['SHIPVIA_REVIEW', 'NO_OPEN_RELEASE', 'BACKORDER_UNCONFIRMED'])

/** poLines the vendor confirmed 0 of (backordered). */
export function backorderLines(c: Confirmation): Set<number> {
  return new Set((c.extracted?.lines ?? []).filter(l => l.confirmedQty === 0).map(l => l.poLine))
}

/** The flags actually worth surfacing: drop the noise types, and suppress
 *  price/ext mismatches on backordered lines (the confirmed price is $0 only
 *  because qty is 0 — not a real pricing discrepancy). */
export function meaningfulFlags(c: Confirmation): ReportFlag[] {
  const bo = backorderLines(c)
  return (c.report?.flags ?? []).filter(f => {
    if (HIDDEN_FLAG_TYPES.has(f.type)) return false
    if ((f.type === 'PRICE_MISMATCH' || f.type === 'EXT_PRICE_MISMATCH')
      && f.poLine != null && bo.has(f.poLine)) return false
    return true
  })
}
